import { apiFetch } from "@/api/client";

/**
 * Rules/content authoring + simulation API (phase2-admin-ui PRD slice).
 *
 * Everything goes through onboarding-api's `/api/v1/authoring/*` BFF — the SPA
 * never talks to rules-and-content (or the rule engine) directly; the BFF
 * checks the user permission and mints the per-target service token.
 *
 * Types mirror `rules_and_content_service.models.api` / `api.rule_authoring`
 * (hand-written, same rationale as `catalogs.ts`).
 */

export const REGIONS = ["US", "EU", "UK", "FR", "GR"] as const;
export type Region = (typeof REGIONS)[number];

/** One rule condition (the ADR-0027 grammar's wire shape — subset we render). */
export interface RuleCondition {
  operator: string;
  attribute: string;
  value?: unknown;
  [key: string]: unknown;
}

export interface TracerRule {
  insight: string;
  type: string;
  related_biomarkers: string[];
  main_related_biomarkers: string[];
  conditions: RuleCondition[];
  priority: number;
  only_inside: boolean;
  [key: string]: unknown;
}

export interface EffectiveRuleSet {
  region: string;
  organization_id: string | null;
  rules: TracerRule[];
}

/** `GET /api/v1/authoring/rules/{region}[?organization_id]` */
export function listRules(region: string, org?: string): Promise<EffectiveRuleSet> {
  const suffix = org ? `?organization_id=${encodeURIComponent(org)}` : "";
  return apiFetch<EffectiveRuleSet>(
    `/api/v1/authoring/rules/${encodeURIComponent(region)}${suffix}`,
  );
}

export interface ContentSummary {
  insight_id: string;
  regions: string[];
  languages: string[];
  lifecycle_stage: string;
  severity: string | null;
  imported: boolean;
  citation_count: number;
  org_override: boolean;
}

export interface ContentListResponse {
  insights: ContentSummary[];
  total: number;
}

/** `GET /api/v1/authoring/content[?organization_id]` */
export function listContent(org?: string): Promise<ContentListResponse> {
  const suffix = org ? `?organization_id=${encodeURIComponent(org)}` : "";
  return apiFetch<ContentListResponse>(`/api/v1/authoring/content${suffix}`);
}

export interface ContentBody {
  title: string | null;
  body: string;
  disclaimer?: string | null;
  [key: string]: unknown;
}

export interface CitationOut {
  id: string;
  title: string;
  year: number;
  url?: string | null;
  [key: string]: unknown;
}

export interface ContentResponse {
  insight: string;
  language: string;
  content: ContentBody;
  citations: CitationOut[];
  /** Languages authored for the requested region — drives the editor's translation tabs. */
  available_languages?: string[];
  [key: string]: unknown;
}

/** `GET /api/v1/authoring/content/{id}?region&lang[&organization_id]` */
export function getContent(
  insightId: string,
  region: string,
  lang = "en",
  org?: string,
): Promise<ContentResponse> {
  const params = new URLSearchParams({ region, lang });
  if (org) params.set("organization_id", org);
  const bare = insightId.replace(/^#/, "");
  return apiFetch<ContentResponse>(
    `/api/v1/authoring/content/${encodeURIComponent(bare)}?${params}`,
  );
}

/**
 * `GET /api/v1/authoring/citations?q=` — the content editor's citation picker.
 * Substring search over the ~7,580-citation governed corpus (id or title).
 */
export function searchCitations(q: string, limit = 20): Promise<CitationOut[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return apiFetch<CitationOut[]>(`/api/v1/authoring/citations?${params.toString()}`);
}

/** A draft citation the editor lints (the TracerCitation shape). */
export interface CitationDraft {
  id: string;
  type: string;
  title: string;
  year?: number | null;
  publisher?: string | null;
  url?: string | null;
  recency_status?: string;
  region_applicability?: string[];
  date_accessed?: string | null;
}

export interface CitationValidation {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  /** True when a citation with this id is already in the global corpus. */
  id_exists: boolean;
  /** Recognised identifier in the url: "doi" | "pmid" | "url" | null. */
  identifier_type: string | null;
  normalized_citation: (CitationDraft & Record<string, unknown>) | null;
}

/**
 * `POST /api/v1/authoring/citations/validate` — dry-run lint of a draft citation
 * (backlog #7). Never writes: the citation corpus is global (single authority)
 * and is edited on the governed git path, so this is validate-only — shape,
 * id-collision, DOI/PMID/URL recognition, ADR-0044 recency-gap advisories.
 */
export function validateCitation(citation: CitationDraft): Promise<CitationValidation> {
  return apiFetch<CitationValidation>("/api/v1/authoring/citations/validate", {
    method: "POST",
    body: JSON.stringify({ citation }),
  });
}

export interface RuleValidation {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  /**
   * The draft with synonym/display-name terms rewritten to canonical symbols;
   * `null` when nothing needed rewriting (or the draft failed). This is what
   * the submit path commits — the corpus stays canonical.
   */
  normalized_rule: TracerRule | null;
  /** Substitutions applied: {"glucose": "GLU"}. */
  resolved_symbols: Record<string, string>;
  /** Terms matching two or more symbols — the author must disambiguate. */
  ambiguous_symbols: Record<string, string[]>;
  /** Near-matches for unresolvable terms. Advisory; never auto-applied. */
  suggestions: Record<string, string[]>;
}

/** `POST /api/v1/authoring/rules/validate` — draft-rule cross-checks, no write. */
export function validateRule(
  rule: Record<string, unknown>,
  region: string,
  org?: string,
): Promise<RuleValidation> {
  return apiFetch<RuleValidation>("/api/v1/authoring/rules/validate", {
    method: "POST",
    body: JSON.stringify({ rule, region, organization_id: org ?? null }),
  });
}

export type ConflictKind =
  | "duplicate"
  | "contradiction_risk"
  | "same_insight_overlap"
  | "co_fire";

export interface RuleConflict {
  insight: string;
  type: string;
  shared_biomarkers: string[];
  kind: ConflictKind;
  /** True when the overlap is provable; false when only possible (advisory). */
  certain: boolean;
  note: string;
  /** Compact rendering of the other rule's conditions. */
  conditions: string[];
}

export interface RuleConflictsResponse {
  conflicts: RuleConflict[];
  counts: Record<string, number>;
  warnings: string[];
}

/**
 * `POST /api/v1/authoring/rules/conflicts` — advisory co-fire / band-overlap
 * check. Surfaces existing rules on the same biomarker whose ranges overlap the
 * draft (the "two readings for one biomarker" hazard), before the MR. Not a gate.
 */
export function checkRuleConflicts(
  rule: Record<string, unknown>,
  region: string,
  org?: string,
): Promise<RuleConflictsResponse> {
  return apiFetch<RuleConflictsResponse>("/api/v1/authoring/rules/conflicts", {
    method: "POST",
    body: JSON.stringify({ rule, region, organization_id: org ?? null }),
  });
}

export interface SimulatedBiomarker {
  name: string;
  value: number;
  unit: string;
}

export interface SimulatedPatient {
  patient_id?: string;
  biomarkers: SimulatedBiomarker[];
  profile?: Record<string, string | number>;
  conditions?: string[];
  medications?: string[];
}

export interface SimulateRequest {
  region: string;
  organization_id?: string | null;
  draft_rule?: Record<string, unknown> | null;
  patient: SimulatedPatient;
}

export interface SimulationRun {
  fired_insight_ids: string[];
  rules_evaluated: number;
  rules_fired: number;
}

export interface BiomarkerReading {
  biomarker: string;
  reading: string;
  severity?: string | null;
  [key: string]: unknown;
}

/** One simulated observation whose unit the biomarker definition does not declare. */
export interface UnrecognizedUnit {
  biomarker: string;
  unit: string;
  declared_units: string[];
}

export interface SimulateResponse {
  baseline: SimulationRun;
  with_draft: SimulationRun | null;
  added_insight_ids: string[];
  removed_insight_ids: string[];
  draft_rule_fired: boolean | null;
  biomarker_readings: BiomarkerReading[];
  unknown_biomarkers: string[];
  /**
   * Observations excluded because their unit is not one the definition declares.
   * The engine would have banded them CONFIDENTLY (see the endpoint's comment), so a
   * non-empty list means the case that ran is not the case that was typed.
   */
  unrecognized_units?: UnrecognizedUnit[];
  /** Synonyms canonicalized before evaluation ({"glucose": "GLU"}). */
  resolved_symbols: Record<string, string>;
  /** Wall-clock of the rule-engine evaluation(s), ms — the authoring latency. */
  elapsed_ms: number;
}

/** `POST /api/v1/authoring/rules/simulate` — dry-run against the real engine. */
export function simulateRules(request: SimulateRequest): Promise<SimulateResponse> {
  return apiFetch<SimulateResponse>("/api/v1/authoring/rules/simulate", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** Why a suggestion matched — drives the menu's explanation line. */
export type MatchKind = "symbol" | "prefix" | "substring" | "synonym" | "fuzzy";

/** A condition attribute is either a biomarker symbol or a profile attribute. */
export type AttributeKind = "biomarker" | "profile";

export interface BiomarkerSuggestion {
  /** Canonical symbol — the identity (ADR-0009); selection inserts THIS. */
  symbol: string;
  display: string;
  abbreviation: string | null;
  /** Primary LOINC; null for groupers/derived values → render an em-dash. */
  loinc: string | null;
  matched_on: MatchKind;
  matched_text: string;
  kind: AttributeKind;
  /** Ordered category vocabulary; empty => numeric (number input, not select). */
  allowed_categories: string[];
}

export interface BiomarkerSuggestResponse {
  suggestions: BiomarkerSuggestion[];
  /** False in ADR-0050 degraded mode — the picker falls back to free text. */
  registry_available: boolean;
}

/**
 * `GET /api/v1/authoring/biomarkers/suggest` — the picker's autocomplete.
 * `includeProfileAttributes` adds `condition`/`pregnant`/… (kind="profile"),
 * which is what the condition builder's *attribute* picker wants.
 */
export function suggestBiomarkers(
  q: string,
  limit = 10,
  includeProfileAttributes = false,
): Promise<BiomarkerSuggestResponse> {
  const query = new URLSearchParams({ q, limit: String(limit) });
  if (includeProfileAttributes) query.set("include_profile_attributes", "true");
  return apiFetch<BiomarkerSuggestResponse>(`/api/v1/authoring/biomarkers/suggest?${query}`);
}

export interface AttributeVocabulary {
  attribute: string;
  /** Sorted, sentinel-free values. Empty for numeric attributes (age). */
  values: string[];
  /** "none" / "n-a" — offered separately, never as a clinical value. */
  sentinels: string[];
}

export interface VocabularyResponse {
  /** "corpus" (descriptive, grows with the rules) or "conditions-list". */
  source: string;
  attributes: AttributeVocabulary[];
}

/** `GET /api/v1/authoring/rules/vocabulary` — value pick-lists per attribute. */
export function getVocabulary(): Promise<VocabularyResponse> {
  return apiFetch<VocabularyResponse>("/api/v1/authoring/rules/vocabulary");
}

export interface ContentHit {
  insight_id: string;
  region: string;
  lang: string;
  field: string;
  snippet: string;
}

export interface SearchResponse {
  rules: TracerRule[];
  content: ContentHit[];
  total_rules: number;
  total_content: number;
  content_truncated: boolean;
  /** Applied term→symbol resolution (synonyms + fuzzy): {"glucose": ["GLU"]}. */
  resolved_biomarkers: Record<string, string[]>;
  /** Applied fuzzy expansion of profile terms: {"diabetis": ["diabetes"]}. */
  resolved_profile: Record<string, string[]>;
}

export interface SearchParams {
  region: string;
  org?: string | undefined;
  /** Comma-separated biomarker symbols; `match` governs all-vs-any. */
  biomarkers?: string | undefined;
  biomarkerMatch?: "all" | "any" | undefined;
  /** Comma-separated profile/condition terms (diabetes, smoker, …). */
  profile?: string | undefined;
  /** Content full-text (min 2 chars). */
  q?: string | undefined;
  /** Synonym + near-match expansion (default true server-side). */
  fuzzy?: boolean | undefined;
}

/**
 * `GET /api/v1/authoring/search` — rules by biomarker/profile predicate +
 * content full-text with snippets; axes compose with AND.
 */
export function searchAuthoring(params: SearchParams): Promise<SearchResponse> {
  const query = new URLSearchParams({ region: params.region });
  if (params.org) query.set("organization_id", params.org);
  if (params.biomarkers) query.set("biomarkers", params.biomarkers);
  if (params.biomarkerMatch) query.set("biomarker_match", params.biomarkerMatch);
  if (params.profile) query.set("profile", params.profile);
  if (params.q) query.set("q", params.q);
  if (params.fuzzy !== undefined) query.set("fuzzy", String(params.fuzzy));
  return apiFetch<SearchResponse>(`/api/v1/authoring/search?${query}`);
}

export interface SubmitResult {
  mr_url: string;
  branch_name: string;
}

/**
 * `POST /api/v1/authoring/{org}/rules` — open an MR adding/updating one
 * org-layer rule (id must be `{org}:#<digits>` per ADR-0008). `region` rides
 * along as the `_region` hint the BFF uses for its validate-before-MR call.
 */
export function submitRule(
  org: string,
  rule: Record<string, unknown>,
  region: string,
): Promise<SubmitResult> {
  return apiFetch<SubmitResult>(`/api/v1/authoring/${encodeURIComponent(org)}/rules`, {
    method: "POST",
    body: JSON.stringify({ ...rule, _region: region }),
  });
}

/** `POST /api/v1/authoring/{org}/content` — MR for one org-layer insight file. */
export function submitContent(
  org: string,
  insightFile: Record<string, unknown>,
): Promise<SubmitResult> {
  return apiFetch<SubmitResult>(`/api/v1/authoring/${encodeURIComponent(org)}/content`, {
    method: "POST",
    body: JSON.stringify(insightFile),
  });
}
